<template>
	<div class="blain-cart">
		<div class="header">
			<h1>
				<span class="logo"></span>
				<span class="text">Cart System Activity</span>
			</h1>
		</div>
		<div class="main-wrapper">
			<div class="top-wrapper">
				<div class="us-map" id="usmap">
				</div>

				<div class="checkout-sessions">
					<h2>Live Checkout Highlights</h2>
					<div class="checkout-header-wrapper">
						<div class="checkout-header">
							<div class="progress-head">Progress</div>
							<div class="destination">Destination</div>
							<div class="subtotal">Subtotal</div>
							<div class="shipping">Shipping</div>
							<div class="tax">Tax</div>
						</div>
					</div>
					<transition-group name="checkout-row" tag="div" class="checkout-wrapper">
						<div class="checkout-row" v-bind:key="'0'" v-if="checkoutHighlights.length === 0">
							<div class="no-activity">No activity</div>
						</div>
						<div class="checkout-row" v-for="c in checkoutHighlights" v-bind:key="c.id">
							<div v-bind:class="['progressbar', getProgressClass(c.markerType)]">
								<span v-bind:style="{ width: c.progressWidth + '%' }">{{ getHighFiveMessage(c) }}</span>
							</div>
							<div class="icons">
								<i v-bind:class="getCheckoutTypeIcon(c)"></i>
								<i v-bind:class="getDeliveryMethodIcon(c.deliveryMethod)"></i>
								<i v-bind:class="getCheckoutDevice(c.checkoutMode)"></i>
								<i class="far fa-fw fa-credit-card" style="opacity: 0.3" v-if="hasNoTender(c.tenderType, c.paymentFailCount)"></i>
								<i v-bind:class="[getTenderColorClass(c), getTenderTypeClass(c)]" v-else></i>
							</div>
							<div class="destination">{{c.destination}}</div>
							<div class="subtotal">{{c.subtotal | asCurrency}}</div>
							<div class="shipping">{{c.shipping | asCurrency}}</div>
							<div class="tax">{{c.salesTax | asCurrency}}</div>
						</div>
					</transition-group>
				</div>
			</div>
			<div class="bottom-wrapper">
				<div class="bottom-header">
					<h2>
						Active Users: {{totalActiveUsers}}
						<span class="web-icon">
							<i class="fas fa-lg fa-globe"></i>
							{{webUsers}}
						</span>
						<span class="ios-icon">
							<i class="fab fa-lg fa-apple"></i>
							{{iosUsers}}
						</span>
						<span class="android-icon">
							<i class="fab fa-lg fa-android"></i>
							{{androidUsers}}
						</span>
						
					</h2>
					<div class="bottom-subhead">
						<h3>Past 2 days</h3>
						<h4>Updates every 15 minutes</h4>
					</div>
				</div>
				<div class="chart-inner-wrapper">
					<canvas id="sessionChart"></canvas>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import Bugsnag from '@bugsnag/js'
	import Chart from 'chart.js';

	let refreshInterval = null;
	let chartCreated = false;
	let myChart = null;
	let myMap = null;
	let mapContainer = null;
	let latest = null;
	import 'jvectormap-next';
	import './us-aea-map';
	import '../../less/jvectormap.css';

	export default {
		name: 'BlainCart',

		data() {
			return {
				activeCarts: {},
				activeCheckouts: {},
				checkoutHighlights: [],
				serverTime: null,
				totalActiveUsers: "-",
				webUsers: "-",
				iosUsers: "-",
				androidUsers: "-"
			};
		},

		filters: {
			asCurrency(val) {
				let num = val.toString().replace(/[$,]/g, '');
				if (isNaN(num) || num === "0") return "-";
				num = Math.floor(num * 100 + 0.50000000001);
				let cents = num % 100;
				num = Math.floor(num / 100).toString();
				if (cents < 10) cents = "0" + cents;
				num = num.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
				return "$" + num + "." + cents;
			}
		},

		mounted() {
			console.log("Blain cart module initiated");
			mapContainer = window.$('#usmap');
			mapContainer.vectorMap({
				map: 'us_aea',
				backgroundColor: '#000',
				zoomOnScroll: false,
				panOnDrag: false,
				regionsSelectable: false,
				markersSelectable: false,
				regionStyle: {
					initial: {
						fill: '#666'
					}
				},
				markerStyle: {
					initial: {
						stroke: "#FFF"
					}
				},
				onRegionOver: function (e) {
					e.preventDefault();
				},
				onRegionTipShow: function (e) {
					e.preventDefault();
				},
				onMarkerOver: function (e) {
					e.preventDefault();
				},
				onMarkerTipShow: function (e) {
					e.preventDefault();
				}
			});
			myMap = mapContainer.vectorMap('get', 'mapObject');

			this.refreshData();
			refreshInterval = setInterval(this.refreshData, 10000);
			window.addEventListener('overscan', this.resizeChart);
		},
		
		destroyed() {
			window.removeEventListener('overscan', this.resizeChart);
			console.log("Stopping Blain cart module");
			clearInterval(refreshInterval);
			chartCreated = false;
			myChart = null;
			mapContainer = null;
		},
		
		methods: {

			refreshData() {
				let self = this;
				this.$http.post('/blain/refresh-cart', {}).then(response => {
					let d = response.body;

					self.serverTime = new Date(d.serverTime);
					if (d.activeUserCounts !== null) {
						self.totalActiveUsers = (d.activeUserCounts.web + d.activeUserCounts.ios + d.activeUserCounts.android).toLocaleString();
						self.webUsers = d.activeUserCounts.web.toLocaleString();
						self.iosUsers = d.activeUserCounts.ios.toLocaleString();
						self.androidUsers = d.activeUserCounts.android.toLocaleString();
					}
					self.updateCarts(d.activeCarts);
					self.updateCheckouts(d.activeCheckouts);

					// Chart data updates
					if (!chartCreated) self.createChart(d.userCountsHistory);
					else self.refreshChart(d.userCountsHistory[d.userCountsHistory.length - 1]);

				}).catch(e => {
					Bugsnag.notify(new Error(e.url), function (event) {
						event.addMetadata('httpResponse', e);
					});
					console.error(e);
				});
			},

			updateCarts(carts) {
				// Iterate through new carts and add new ones
				for (let x = 0, len = carts.length; x < len; x++) {
					let c = carts[x];
					if (!this.activeCarts.hasOwnProperty("cart" + c.id)) {
						this.activeCarts["cart" + c.id] = c;
						myMap.addMarker("cart" + c.id, {
							latLng: [ c.ipGeoLatitude, c.ipGeoLongitude ],
							name: c.ipGeoCity + ", " + c.ipGeoRegion,
							style: { fill: "#AAA" }
						});
					}
				}

				// Iterate through old carts and remove old ones
				let keyNames = Object.keys(this.activeCarts);
				let toRemove = []
				for (let x = 0, len = keyNames.length; x < len; x++) {
					let i = keyNames[x];
					if (!carts.some(c => "cart" + c.id === i)) {
						if (myMap.markers.hasOwnProperty(i)) toRemove.push(i);
						delete this.activeCarts[i];
					}
				}

				// Update map markers
				if (toRemove.length > 0) myMap.removeMarkers(toRemove);
			},

			updateCheckouts(checkouts) {
				let self = this;
				for (let x = 0, len = checkouts.length; x < len; x++) {
					let c = checkouts[x];
					if (c.latitude === null || c.longitude === null) continue;
					c.markerType = self.getMarkerType(c);
					c.markerSort = self.getMarkerSort(c.markerType);
					c.progressWidth = self.getProgressWidth(c.currentStatus);
					if (!this.activeCheckouts.hasOwnProperty(c.markerType + c.id)) {
						this.activeCheckouts[c.markerType + c.id] = c;
						myMap.addMarker(c.markerType + c.id, {
							latLng: [ c.latitude, c.longitude ],
							name: c.destination,
							style: { fill: self.getMarkerFill(c.markerType) }
						});
					}
				}

				// Iterate through old carts and remove old ones
				let keyNames = Object.keys(this.activeCheckouts);
				let toRemove = []
				for (let x = 0, len = keyNames.length; x < len; x++) {
					let i = keyNames[x];
					if (!checkouts.some(c => c.markerType + c.id === i)) {
						if (myMap.markers.hasOwnProperty(i)) toRemove.push(i);
						delete this.activeCheckouts[i];
					}
				}

				// Update map markers
				if (toRemove.length > 0) myMap.removeMarkers(toRemove);

				// Create checkout highlights based on what's most interesting
				let tmp = window.orderBy(checkouts, ['MarkerSort', 'ProgressWidth'], ['asc', 'desc']);
				let ids = tmp.map(c => c.id);
				if (ids.length > 16) ids = ids.slice(0, 16);
				let highlights = [];
				let len = checkouts.length;
				for (let x = 0; x < len; x++) {
					let c = checkouts[x];
					if (ids.indexOf(c.id) !== -1) highlights.push(c);
				}

				this.checkoutHighlights = highlights;
			},

			getMarkerType(checkout) {
				if (checkout.currentStatus === "Order Submitted") return "done";
				let itemDate = new Date(checkout.statusTimestamp);
				let now = new Date(this.serverTime.getTime());
				let old = new Date(this.serverTime.getTime());
				let older = new Date(this.serverTime.getTime());
				let oldest = new Date(this.serverTime.getTime());
				old.setMinutes(now.getMinutes() - 2);
				older.setMinutes(now.getMinutes() - 5);
				oldest.setMinutes(now.getMinutes() - 7);
				if (itemDate < oldest) return "oldest";
				if (itemDate < older) return "older";
				if (itemDate < old) return "old";
				return "current";
			},

			getMarkerSort(markerType) {
				switch (markerType) {
					case "done":
						return 1;
					case "current":
						return 2;
					case "old":
						return 3;
					case "older":
						return 4;
					case "oldest":
						return 5;
					default:
						return 6;
				}
			},

			getMarkerFill(markerType) {
				switch (markerType) {
					case "done":
						return "#090";
					case "oldest":
						return "#c00";
					case "older":
						return "#ea0";
					case "old":
						return "#08c6ff";
					default:
						return "#3683ff";
				}
			},

			getProgressWidth(val) {
				switch (val) {
					case "Begin Secure Checkout":
					case "Begin PayPal Checkout":
					case "Begin Apple Pay Checkout":
						return 5;
					case "Begin Custom Order Checkout":
						return 10;
					case "Guest Info":
						return 15;
					case "Customer Login":
						return 20;
					case "Customer Info":
						return 25;
					case "Edit Shipping Address":
						return 30;
					case "Different Shipping Address":
						return 35;
					case "Address Validation":
						return 40;
					case "eCommerce State Exclusions":
						return 45;
					case "Custom Order Required":
						return 50;
					case "Ship Method/Pickup Info":
						return 55;
					case "Promo Code Entry":
						return 65;
					case "Guest Payment":
					case "Customer Payment":
					case "Replacement Order Authorization":
						return 70;
					case "Enter PayPal Tender":
					case "Edit Saved Credit Card":
						return 75;
					case "Verify Credit Card":
						return 80;
					case "Final Confirmation":
						return 85;
					case "Placing Order":
						return 90;
					case "Submit PLCC Payment":
						return 95;
					case "PLCC Success":
						return 97;
					case "Order Submitted":
					case "Order Queued":
						return 100;
					default:
						return 2;
				}
			},

			getProgressClass(markerType) {
				switch (markerType) {
					case "done":
						return "green";
					case "oldest":
						return "red";
					case "older":
						return "yellow";
					case "old":
						return "ltblue";
					default:
						return "";
				}
			},

			getHighFiveMessage(checkout) {
				if (checkout.currentStatus !== "Order Submitted") return null;
				let id = checkout.orderID.toString();
				let lastIndex = id.length - 1;
				if (id.indexOf("1", lastIndex) !== -1) return "Cha-ching!";
				if (id.indexOf("2", lastIndex) !== -1) return "Awesome!";
				if (id.indexOf("3", lastIndex) !== -1) return "Done!";
				if (id.indexOf("4", lastIndex) !== -1) return "Woo hoo!";
				if (id.indexOf("5", lastIndex) !== -1) return "Ding!";
				if (id.indexOf("6", lastIndex) !== -1) return "It's in!";
				if (id.indexOf("7", lastIndex) !== -1) return "Great!";
				if (id.indexOf("8", lastIndex) !== -1) return "Lovely!";
				if (id.indexOf("9", lastIndex) !== -1) return "Boo Yah!";
				return "Fabulous!";
			},

			getCheckoutTypeIcon(rec) {
				if (rec.isReseller) return "fas fa-fw fa-user-tag";
				switch (rec.checkoutAs) {
					case "CallCenter":
						return "fas fa-fw fa-phone";
					case "PayPalExpress":
						return "fab fa-fw fa-paypal";
					case "ApplePay":
						return "fab fa-fw fa-apple";
					case "Custom":
						return "fas fa-fw fa-wrench";
					case "Customer":
						return "fas fa-fw fa-user";
					default:
						return "fas fa-fw fa-users";
				}
			},

			getDeliveryMethodIcon(deliveryMethod) {
				switch (deliveryMethod) {
					case "Pickup":
						return "fas fa-fw fa-car";
					case "Mixed":
						return "fas fa-fw fa-ellipsis-h";
					default:
						return "fas fa-fw fa-truck";
				}
			},

			getCheckoutDevice(checkoutMode) {
				switch (checkoutMode) {
					case "Mobile":
						return "fas fa-fw fa-mobile-alt";
					case "App":
						return "fab fa-fw fa-react";
					case "iOS":
						return "fab fa-fw fa-apple";
					case "Android":
						return "fab fa-fw fa-android";
					default:
						return "fas fa-fw fa-laptop";
				}
			},

			hasNoTender(tenderType, paymentFailCount) {
				return (tenderType === "" || tenderType === null) && (paymentFailCount.toString() === "0" || paymentFailCount === null || paymentFailCount === "");
			},

			getTenderColorClass(rec) {
				if (rec.currentStatus === "Order Submitted") return null;
				if (rec.paymentFailCount > 5) return "red";
				if (rec.paymentFailCount > 1) return "orange";
				if (rec.paymentFailCount > 0) return "yellow";
				return null;
			},

			getTenderTypeClass(rec) {
				switch(rec.tenderType) {
					case "PLCC":
						return "fas fa-fw fa-credit-card";
					case "PayPal":
						return "fab fa-fw fa-cc-paypal";
					case "Apple Pay":
						return "fab fa-fw fa-cc-apple-pay";
					case "Gift Card":
						return "fas fa-fw fa-gift";
					case "Visa":
						return "fab fa-fw fa-cc-visa";
					case "MasterCard":
						return "fab fa-fw fa-cc-mastercard";
					case "Discover":
						return "fab fa-fw fa-cc-discover";
					case "American Express":
						return "fab fa-fw fa-cc-amex";
					default:
						return "fas fa-fw fa-credit-card";
				}
			},

			makeLabels(chartData) {
				let labels = [];
				for (let x = 0, len = chartData.length; x < len; x++) {
				}
				return labels;
			},

			createChart(sessionHistory) {
				let ctx = document.getElementById("sessionChart");
				let webData = [];
				let iosData = [];
				let androidData = [];
				let labels = [];
				for (let x = 0, len = sessionHistory.length; x < len; x++) {
					labels.push(sessionHistory[x].timestamp);
					webData.push(sessionHistory[x].web);
					iosData.push(sessionHistory[x].ios);
					androidData.push(sessionHistory[x].android);
				}
				latest = sessionHistory[sessionHistory.length - 1];
				
				myChart = new Chart(ctx, {
					type: 'line',
					data: {
						labels: labels,
						datasets: [
							{
								label: "Android",
								backgroundColor: "rgba(0,153,0,0.5)",
								borderColor: "rgba(0,153,0,1)",
								pointBorderColor: "rgba(0,153,0,0.0)",
								pointBackgroundColor: "rgba(0,153,0,0.0)",
								pointBorderWidth: 1,
								pointRadius: 1,
								lineTension: 0,
								data: androidData
							},
							{
								label: "iOS",
								backgroundColor: "rgba(255,102,102,0.5)",
								borderColor: "rgba(255,102,102,1)",
								pointBorderColor: "rgba(255,102,102,0.0)",
								pointBackgroundColor: "rgba(255,102,102,0.0)",
								pointBorderWidth: 1,
								pointRadius: 1,
								lineTension: 0,
								data: iosData
							},
							{
								label: "Web",
								backgroundColor: "rgba(54,131,255,0.5)",
								borderColor: "rgba(54,131,255,1)",
								pointBorderColor: "rgba(52,131,255,0.0)",
								pointBackgroundColor: "rgba(52,131,255,0.0)",
								pointBorderWidth: 1,
								pointRadius: 1,
								lineTension: 0,
								data: webData
							},
						]
					},
					options: {
						responsive: true,
						maintainAspectRatio: false,
						legend: {
							display: false
						},
						tooltips: {
							enabled: false
						},
						animation: {
							duration: 0
						},
						scales: {
							yAxes: [{
								stacked: true,
								ticks: {
                    				fontColor: "white"
								},
								gridLines: {
									display: true,
									color: "rgba(255, 255, 255, 0.3)"
								}
							}],
							xAxes: [{
								stacked: true,
								type: 'time',
								time: {
									displayFormats: {
										day: "H:MM:SS"
									}
								},
								ticks: {
                    				fontColor: "white"
								},
								gridLines: {
									display: false,
									color: "rgba(255, 255, 255, 0.3)"
								}
							}]
						}
					}
				});

				chartCreated = true;
			},

			resizeChart() {
				setTimeout(function() {
					myChart.resize();
				}, 2000);
			},

			refreshChart(currentRec) {
				if (currentRec.timestamp === latest.timestamp) return;
				let dataLength = myChart.data.labels.length;
				latest = currentRec;
				if (dataLength > 192) myChart.data.labels.shift();
				myChart.data.labels.push(latest.timestamp);
				myChart.data.datasets.forEach(d => {
					if (dataLength > 192) d.data.shift();
					if (d.label === "Android") d.data.push(latest.android);
					else if (d.label === "iOS") d.data.push(latest.ios);
					else if (d.label === "Web") d.data.push(latest.web);
					console.log("User counts updated as of " + latest.timestamp);
    			});
				myChart.update();
			}
		}
	}
</script>

<style lang="less">
	@import '../../less/_variables.less';
	@import '../../less/progressbar.less';

	@bg-summary: #333;

	.blain-cart {
		font-family: @font-main;
		padding: 0;
		height: 100%;
		color: #fff;
		display: flex;
		flex-direction: column;

		.header {
			flex: 0 0 auto;
			width: 100%;
			padding: 2% 2% 1%;
		}

		h1 {
			display: flex;
			justify-content: flex-start;
			align-items: center;
			margin: 0;
			font-size: 2.5em;
			line-height: 1em;
			height: 100%;

			span.logo {
				background-image: url(images/blain.svg);
				background-size: contain;
				background-position: center center;
				background-repeat: no-repeat;
				width: 3.5em;
				height: 1.7em;
			}

			span.text {
				text-transform: uppercase;
				padding-left: 0.5em;
			}
		}

		.main-wrapper {
			flex: 0 1 auto;
			display: flex;
			flex-direction: column;
			width: 100%;
		}

		.top-wrapper {
			display: flex;
			justify-content: space-between;
			height: 70%;
			flex: 0 0 70%;
			padding: 0 2% 0.5%;
			width: 100%;
		}

		.us-map {
			width: 53%;
			height: 100%;
		}

		.checkout-sessions {
			border-radius: @border-radius;
			background-color: @bg-summary;
			width: 45%;
			overflow: hidden;
			display: flex;
			flex-direction: column;

			h2 {
				text-transform: uppercase;
				padding: 0.5em 1em 0;
				margin: 0;
				font-size: 1.8em;
				text-align: center;
				flex: 0 0 auto;
			}
		}

		.checkout-header-wrapper {
			display: flex;
			flex-flow: column wrap;
			justify-content: flex-start;
			padding-top: 0.3em;
			flex: 0 0 auto;
		}

		.checkout-wrapper {
			position: relative;
			display: flex;
			flex-flow: column nowrap;
			justify-content: flex-start;
			flex: 1 1 auto;
		}

		.checkout-header {
			text-transform: uppercase;
		}

		.checkout-row {
			transition: all 0.5s;
		}

		.checkout-row-enter,
		.checkout-row-leave-to {
			opacity: 0;
			/* transform: translateX(30px); */
		}

		.checkout-row-leave-active {
			/*
			position: absolute;
			width: 100%;
			*/
			height: 0;
		}

		.checkout-header,
		.checkout-row {
			display: flex;
			justify-content: space-between;
			align-items: flex-start;
			padding: 0.3em 1.5em;
			font-weight: 300;
			font-size: 0.9em;

			.progress-head {
				width: 35%;
			}

			.progressbar {
				width: 15%;
				border-radius: 0.3em;
				height: 1.3em;

				> span {
					border-radius: 0.3em;
					font-size: 0.8em;
				}
			}

			.icons {
				width: 20%;
				padding: 0 0.8em;
				white-space: nowrap;
			}

			.no-activity {
				width: 100%;
				text-align: center;
				font-weight: 700;
				color: #aaa;
			}

			.destination {
				width: 23%;
				white-space: nowrap;
				text-overflow: ellipsis;
				overflow: hidden;
			}

			.subtotal {
				width: 14%;
				text-align: right;
				padding-left: 0.5em;
				white-space: nowrap;
				text-overflow: ellipsis;
				overflow: hidden;
			}

			.shipping {
				width: 14%;
				text-align: right;
				padding-left: 0.5em;
				white-space: nowrap;
				text-overflow: ellipsis;
				overflow: hidden;
			}

			.tax {
				width: 14%;
				text-align: right;
				padding-left: 0.5em;
				white-space: nowrap;
				text-overflow: ellipsis;
				overflow: hidden;
			}
		}

		.bottom-wrapper {
			display: flex;
			justify-content: space-between;
			flex-direction: column;
			flex: 1 1 auto;
			padding: 0.5% 2% 2%;
			width: 100%;

			.bottom-header {
				display: flex;
				justify-content: space-between;
				flex: 0 0 auto;
				padding: 0 0 0.5em;
				align-items: flex-end;
			}

			h2 {
				text-transform: uppercase;
				padding: 0;
				margin: 0;
				font-size: 1.8em;
				text-align: left;
			}

			.bottom-subhead {
				text-align: right;
			}

			h3 {
				padding: 0;
				margin: 0;
				font-size: 1em;
			}

			h4 {
				padding: 0;
				margin: 0;
				font-weight: 300;
				font-size: 0.8em;
			}

			.chart-inner-wrapper {
				flex: 1 1 auto;
				padding: 0 1em;
			}
		}
	}

	.jvectormap-zoomin,
	.jvectormap-zoomout,
	.jvectormap-tip {
		display: none;
	}

	.jvectormap-marker {
		animation: 1s marker 1;
	}

	.red {
		color: #f66;
	}

	.orange {
		color: #f93;
	}

	.yellow {
		color: #ff0;
	}

	.web-icon,
	.android-icon,
	.ios-icon {
		display: inline-block;
		margin: 0 0 0 10px;
		font-size: 0.8em;
	}

	.web-icon {
		color: rgba(54,131,255,1);
	}

	.ios-icon {
		color: rgba(255,102,102,1);
	}

	.android-icon {
		color: rgba(0,153,0,1);
	}
	
	@keyframes marker {
		from {
			stroke-width: 20;
			stroke-opacity: 0;
			fill-opacity: 0;
		}

		to {
			stroke-width: 1;
			stroke-opacity: 1;
			fill-opacity: 1;
		}
	}
</style>


// WEBPACK FOOTER //
// blain-cart.vue?1e554657